import diff from 'deep-diff';
import PropTypes from 'prop-types';
import React from 'react';
import { convertFromRaw } from '@ublend-npm/th90-editor';
import Editor from '../../common/EditorPlus/EditorPlus.container';
import Reply from './Reply.container';
import { isEmptyEditorContent } from '../utils';
import { Container } from './WriteZone.styled';
import { removeUnfinishedFileBlocksFromContent } from '../../../../utils/editorUtils';
import { UP_ARROW } from '../../../../utils/keyCodes';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const paddings = {
  top: 24,
  right: 0,
  bottom: 0,
  left: 24,
};

const pluginConfig = {
  addBlock: { inverted: true },
  textFormatter: { verticalOffset: 30 },
  assignments: { disableControl: true },
};

class WriteZone extends React.PureComponent {
  constructor(props) {
    super(props);
    this.editor = null;

    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onReturn = this.onReturn.bind(this);
    this.focus = this.focus.bind(this);
    this.setEditor = this.setEditor.bind(this);
    this.updateDraft = this.updateDraft.bind(this);

    this.lastContentOfEditor = null;
    this.refocusInterval = null;
  }

  componentDidMount() {
    this.loadDraftAndFocusEditor();
  }

  async componentDidUpdate(prevProps) {
    const { conversationId, reply } = this.props;
    const conversationChanged = prevProps.conversationId !== conversationId;

    if (conversationChanged) {
      this.updateDraft(prevProps.conversationId);
      this.loadDraftAndFocusEditor();
    }
    if (reply && reply.id) {
      await this.focus();
    }
  }

  componentWillUnmount() {
    const { conversationId } = this.props;
    this.updateDraft(conversationId);
  }

  onChange(event) {
    const js = event.toJS();
    const { sendTyping } = this.props;
    if (js.lastChangeType !== null && js.currentContent) {
      const contentDiff = diff(
        js.currentContent,
        this.lastContentOfEditor || {}
      );
      if (contentDiff !== undefined) {
        this.lastContentOfEditor = js.currentContent;
        if (isEmptyEditorContent(this.lastContentOfEditor)) {
          sendTyping();
        }
      }
    }
  }

  onKeyDown(event) {
    const { keyCode } = event;
    const { onEdit } = this.props;
    if (
      keyCode === UP_ARROW &&
      isEmptyEditorContent(this.lastContentOfEditor)
    ) {
      onEdit();
    }
  }

  onReturn() {
    if (!isEmptyEditorContent(this.lastContentOfEditor)) {
      const content = this.editor.export();
      const { onReturn, onMessageSubmit } = this.props;

      onReturn(content);
      onMessageSubmit();
    }

    this.clearAndFocusEditor();
  }

  setEditor(value) {
    this.editor = value;
  }

  updateDraft(conversationId) {
    const { updateDraft: onUpdateDraft } = this.props;
    const isEmpty = isEmptyEditorContent(this.lastContentOfEditor);
    const exportedContent = !isEmpty
      ? removeUnfinishedFileBlocksFromContent(this.editor.export())
      : undefined;
    onUpdateDraft(conversationId, exportedContent);
  }

  async clearAndFocusEditor() {
    this.editor.clear();
    await this.focus();
  }

  async loadDraftAndFocusEditor() {
    const { draft } = this.props;
    if (draft) {
      this.editor.import(draft);
      this.lastContentOfEditor = convertFromRaw(draft);
    } else {
      this.editor.clear();
    }
    await this.focus();
  }

  async focus() {
    if (this.editor) {
      this.editor.focus();
      await delay(0);
    }
    if (this.editor) {
      this.editor.focus();
    }
  }

  render() {
    const { onHeightChange, placeholder, reply, htmlAttributes } = this.props;
    return (
      <Container
        role="textbox"
        onKeyDown={this.onKeyDown}
        tabIndex={-1}
        {...htmlAttributes}
      >
        {reply && <Reply />}
        <Editor
          id="conversation-editor"
          wrappedRef={this.setEditor}
          whiteBackground
          maxEditorHeight="60vh"
          paddings={paddings}
          pluginsConfigs={pluginConfig}
          type="inbox"
          placeholder={placeholder}
          onChange={this.onChange}
          onHeightChange={onHeightChange}
          handleReturn={this.onReturn}
          focusAfterClear
        />
      </Container>
    );
  }
}

WriteZone.propTypes = {
  conversationId: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onHeightChange: PropTypes.func.isRequired,
  onMessageSubmit: PropTypes.func.isRequired,
  onReturn: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  sendTyping: PropTypes.func.isRequired,
  draft: PropTypes.shape({}),
  updateDraft: PropTypes.func.isRequired,
  reply: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  htmlAttributes: PropTypes.shape({}),
};

WriteZone.defaultProps = {
  draft: null,
  reply: null,
  htmlAttributes: {},
};

export default WriteZone;
